/**
 * Reglas de la moneda del negocio.
 *
 * Gemelo de `zona.ts`, y por el mismo motivo: son datos del **negocio**, no del
 * equipo. El navegador sabe en qué país está la persona; lo que hace falta
 * saber es en qué país está la tienda.
 *
 * Aquí vive solo el formato. El cálculo es del servidor: el importe que llega
 * ya viene redondeado con las reglas del país, y esto solo decide cómo se
 * escribe. Redondear también del lado del navegador sería la manera más rápida
 * de que el ticket y la caja dejen de coincidir.
 */

export interface MonedaNegocio {
    /** Locale para `Intl`: decide los separadores de miles y decimales. */
    idioma: string
    simbolo: string
    decimales: number
}

const POR_DEFECTO: MonedaNegocio = { idioma: 'es-CL', simbolo: '$', decimales: 0 }

/*
 * El idioma sale de la región y no del código de moneda porque es una
 * propiedad del país: en Chile los miles se separan con punto y en Perú con
 * coma. Formatear soles con separadores chilenos no está "casi bien": "1.490"
 * son mil cuatrocientos noventa en Santiago y uno con cuarenta y nueve en Lima.
 */
const IDIOMAS: Record<string, string> = { CL: 'es-CL', PE: 'es-PE' }
const IDIOMAS_POR_MONEDA: Record<string, string> = { CLP: 'es-CL', PEN: 'es-PE' }

let actual: MonedaNegocio = POR_DEFECTO

/** La fija el arranque de la sesión, con lo que responde /configuracion. */
export function fijarMonedaNegocio(configuracion: {
    region?: string | null
    codigoMoneda?: string | null
    simboloMoneda?: string | null
    decimalesMoneda?: number | null
}): void {
    const decimales = configuracion.decimalesMoneda

    actual = {
        idioma:
            IDIOMAS[configuracion.region ?? ''] ??
            IDIOMAS_POR_MONEDA[configuracion.codigoMoneda ?? ''] ??
            POR_DEFECTO.idioma,

        simbolo: configuracion.simboloMoneda || POR_DEFECTO.simbolo,

        // Un valor fuera de rango vendría de una configuración rota; antes que
        // dejar la pantalla en blanco con un error de Intl, se usa el de
        // respaldo.
        decimales:
            typeof decimales === 'number' && decimales >= 0 && decimales <= 4
                ? decimales
                : POR_DEFECTO.decimales,
    }

    // Tambien el idioma del documento: lo usan el lector de pantalla, el
    // corrector del teclado y el traductor del navegador.
    document.documentElement.lang = actual.idioma
}

export function monedaNegocio(): MonedaNegocio {
    return actual
}

/** Idioma del negocio, para todo lo que se formatee con `Intl`. */
export function idiomaNegocio(): string {
    return actual.idioma
}
